import { useQuery } from '@tanstack/react-query';
import type { paths } from '../../../generated/apiClient';
import { repositoryGetter } from '../../../repository/container';
import { hasSessionCookieAuthCredential } from '../../../utils';
import { UseQueryDefaultOptions } from '../common';
import { useActiveGrantExpiryRefresh } from './activeGrantExpiry';

const repository = repositoryGetter.Auth();
const apiPath: Extract<keyof paths, '/global-navi/get-bootstrap-v2'> = '/global-navi/get-bootstrap-v2';

type Props = {
  uid: string | undefined;
  /** 省略可。未指定なら account のみの GlobalNaviDataV2 を返す。 */
  projectId: string | undefined;
  enabled?: boolean | undefined;
};

export const queryKey = (props: Props) => [apiPath, props];

/**
 * フロント初期化用 bootstrap (Navi Input V2)。
 * `POST /global-navi/get-bootstrap-v2` を叩き、常に `{ authInfo, globalNavi }` を返す。
 *
 * `uid` が未定なら fetch しない。`projectId` は optional（無いときは account-only）。
 * 詳細は data-model.md / navi-input-v2-scope-split.md を参照。
 */
export const _getBootstrap = (props: Props) => {
  const { uid, projectId } = props;
  const enabled = props.enabled ?? (uid !== undefined || hasSessionCookieAuthCredential());

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: queryKey(props),
    queryFn: async ({ signal }) => {
      if (!enabled) return null;
      return repository.getBootstrap({ ...(projectId ? { projectId } : {}), signal });
    },
    ...UseQueryDefaultOptions,
    throwOnError: false,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    enabled,
  });

  const authInfo = useActiveGrantExpiryRefresh({ authInfo: data?.authInfo, refetch });

  return {
    authInfo,
    globalNavi: data?.globalNavi,
    isPending,
    isError,
    error,
    refetch,
  };
};
